import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertBetaAccess } from "@/lib/beta-access";
import { LISTING_TYPES, type ListingType } from "@/lib/listing-meta";
import { requireCompany } from "@/lib/company-access";
import { assertPublicRateLimit } from "@/lib/rate-limit.server";

// Business Exchange: structured deal listings across every industry —
// sell/source products, raise/deploy capital (with real financials),
// offer/find services. New listings are embedded and matched against
// complementary listings; both sides get a match record and an email.
// Matching itself lives in exchange-match.server.ts (also used by the
// nightly cron at /api/cron/match-listings).

export { LISTING_TYPES, type ListingType };

const MAX_ACTIVE_LISTINGS = 20;
const CURRENCIES = ["USD", "EUR", "CHF", "GBP"] as const;

const ListingInput = z.object({
  type: z.enum(LISTING_TYPES),
  title: z.string().trim().min(4).max(120),
  description: z.string().trim().max(2000).optional(),
  industry: z.string().trim().max(80).optional(),
  country: z.string().trim().max(80).optional(),
  amountMin: z.number().nonnegative().finite().optional(),
  amountMax: z.number().nonnegative().finite().optional(),
  currency: z.enum(CURRENCIES).default("USD"),
  attachmentUrl: z.string().url().max(1000).optional(),
  attachmentName: z.string().trim().max(160).optional(),
  details: z
    .record(z.string().max(60), z.union([z.string().max(300), z.number()]))
    .refine((d) => Object.keys(d).length <= 16, "Too many detail fields")
    .default({}),
});

function embeddingText(l: z.infer<typeof ListingInput>): string {
  const facts = Object.entries(l.details)
    .map(([k, v]) => `${k}: ${v}`)
    .join("; ");
  return [
    l.type.replace("_", " "),
    l.title,
    l.description ?? "",
    l.industry ? `Industry: ${l.industry}` : "",
    l.country ? `Country: ${l.country}` : "",
    facts,
  ]
    .filter(Boolean)
    .join("\n")
    .slice(0, 4000);
}

function publicClient() {
  return createClient<Database>(process.env.SUPABASE_URL!, process.env.SUPABASE_PUBLISHABLE_KEY!, {
    auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
  });
}

export const createListing = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => ListingInput.parse(d))
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    if (data.amountMin != null && data.amountMax != null && data.amountMax < data.amountMin) {
      throw new Error("Maximum amount must be at least the minimum.");
    }
    const access = await requireCompany(context.supabase, context.userId);
    const { data: company } = await context.supabase
      .from("companies")
      .select("id, name")
      .eq("id", access.id)
      .maybeSingle();
    if (!company) throw new Error("Create your company first");

    const { count } = await context.supabase
      .from("listings")
      .select("id", { count: "exact", head: true })
      .eq("company_id", company.id)
      .neq("status", "closed");
    if ((count ?? 0) >= MAX_ACTIVE_LISTINGS) {
      throw new Error(`Listing limit reached (${MAX_ACTIVE_LISTINGS}). Close old listings first.`);
    }

    const { data: listing, error } = await context.supabase
      .from("listings")
      .insert({
        company_id: company.id,
        user_id: context.userId,
        type: data.type,
        title: data.title,
        description: data.description ?? null,
        industry: data.industry ?? null,
        country: data.country ?? null,
        amount_min: data.amountMin ?? null,
        amount_max: data.amountMax ?? null,
        currency: data.currency,
        attachment_url: data.attachmentUrl ?? null,
        attachment_name: data.attachmentName ?? null,
        details: data.details,
      })
      .select("id, company_id, user_id, type, title, description, details")
      .single();
    if (error) throw error;

    // Embed + match server-side (admin: embedding update and cross-company match).
    const key = process.env.LOVABLE_API_KEY;
    let matchCount = 0;
    if (key) {
      const { embedText, runListingMatch } = await import("@/lib/exchange-match.server");
      const vector = await embedText(key, embeddingText(data));
      if (vector) {
        const embStr = `[${vector.join(",")}]`;
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        await supabaseAdmin.from("listings").update({ embedding: embStr }).eq("id", listing.id);
        matchCount = await runListingMatch({ ...listing, embedding: embStr }, company.name, key);
      }
    }

    return { listingId: listing.id, matches: matchCount };
  });

export const listMyListings = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId).catch(() => null);
    if (!access) return { listings: [], matches: [], counterparts: {}, userId: context.userId };
    const { data: listings, error } = await context.supabase
      .from("listings")
      .select(
        "id, type, title, description, industry, country, amount_min, amount_max, currency, details, status, is_public, attachment_url, attachment_name, created_at",
      )
      .eq("company_id", access.id)
      .order("created_at", { ascending: false })
      .limit(50);
    if (error) throw error;

    // Matches where the caller owns either side (RLS scopes rows).
    const { data: matches } = await context.supabase
      .from("listing_matches")
      .select(
        "id, listing_a, listing_b, company_a, company_b, owner_a, owner_b, score, reason, status, created_at",
      )
      .order("score", { ascending: false })
      .limit(200);

    // Enrich counterpart listings + company names via admin (counterparts may
    // be private to RLS but were public at match time).
    const mine = new Set((listings ?? []).map((l) => l.id));
    const counterpartIds = Array.from(
      new Set(
        (matches ?? []).flatMap((m) => [m.listing_a, m.listing_b]).filter((id) => !mine.has(id)),
      ),
    );
    let counterparts: Record<string, any> = {};
    if (counterpartIds.length > 0) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { data: rows } = await supabaseAdmin
        .from("listings")
        .select(
          "id, type, title, description, industry, country, amount_min, amount_max, currency, details, status, company_id, attachment_url, attachment_name, companies(name)",
        )
        .in("id", counterpartIds);
      const { signAttachments } = await import("@/lib/listing-attachments.server");
      const signedRows = await signAttachments((rows ?? []) as any[]);
      counterparts = Object.fromEntries(
        (signedRows ?? []).map((r: any) => [
          r.id,
          { ...r, company_name: r.companies?.name ?? "Company", companies: undefined },
        ]),
      );
    }

    const { signAttachments } = await import("@/lib/listing-attachments.server");
    return {
      listings: await signAttachments((listings ?? []) as any[]),
      matches: matches ?? [],
      counterparts,
      userId: context.userId,
    };
  });

export const updateListingStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z
      .object({
        id: z.string().uuid(),
        status: z.enum(["active", "paused", "closed"]),
      })
      .parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const access = await requireCompany(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("listings")
      .update({ status: data.status, updated_at: new Date().toISOString() })
      .eq("id", data.id)
      .eq("company_id", access.id);
    if (error) throw error;
    return { ok: true };
  });

export const refreshListingMatches = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const key = process.env.LOVABLE_API_KEY;
    if (!key) throw new Error("Server not configured");
    const access = await requireCompany(context.supabase, context.userId);
    const { data: listing } = await context.supabase
      .from("listings")
      .select("id, company_id, user_id, type, title, description, details, embedding, status")
      .eq("id", data.id)
      .eq("company_id", access.id)
      .maybeSingle();
    if (!listing) throw new Error("Listing not found");
    if (listing.status !== "active") throw new Error("Activate the listing first");
    const { data: company } = await context.supabase
      .from("companies")
      .select("name")
      .eq("id", listing.company_id)
      .maybeSingle();
    const { runListingMatch } = await import("@/lib/exchange-match.server");
    const matches = await runListingMatch(listing as any, company?.name ?? "A company", key);
    return { matches };
  });

export const updateMatchStatus = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) =>
    z.object({ id: z.string().uuid(), status: z.enum(["new", "contacted", "dismissed"]) }).parse(d),
  )
  .handler(async ({ context, data }) => {
    await assertBetaAccess(context.supabase, context.userId);
    const { error } = await context.supabase
      .from("listing_matches")
      .update({ status: data.status })
      .eq("id", data.id);
    if (error) throw error;
    return { ok: true };
  });

// Public browse across the network — no auth, anon RLS shows only
// public + active listings.
export const listExchange = createServerFn({ method: "POST" })
  .inputValidator((d) =>
    z
      .object({
        type: z.enum(LISTING_TYPES).optional(),
        q: z.string().trim().max(120).optional(),
        industry: z.string().trim().max(80).optional(),
        country: z.string().trim().max(80).optional(),
      })
      .partial()
      .parse(d ?? {}),
  )
  .handler(async ({ data }) => {
    await assertPublicRateLimit("exchange:list", 60);
    const supabase = publicClient();
    let query = supabase
      .from("listings")
      .select(
        "id, type, title, description, industry, country, amount_min, amount_max, currency, details, created_at, company_id, attachment_url, attachment_name, companies(name)",
      )
      .eq("is_public", true)
      .eq("status", "active")
      .order("created_at", { ascending: false })
      .limit(60);
    if (data.type) query = query.eq("type", data.type);
    if (data.q) query = query.ilike("title", `%${data.q}%`);
    if (data.industry) query = query.ilike("industry", `%${data.industry}%`);
    if (data.country) query = query.ilike("country", `%${data.country}%`);
    const { data: rows, error } = await query;
    if (error) throw error;
    const { signAttachments } = await import("@/lib/listing-attachments.server");
    const signed = await signAttachments((rows ?? []) as any[]);
    return {
      listings: signed.map((r: any) => ({
        ...r,
        company_name: r.companies?.name ?? "Company",
        companies: undefined,
      })),
    };
  });

// Public single-listing view for /deals/$id (SEO page).
export const getPublicListing = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data }) => {
    await assertPublicRateLimit("exchange:listing", 60);
    const supabase = publicClient();
    const { data: row, error } = await supabase
      .from("listings")
      .select(
        "id, type, title, description, industry, country, amount_min, amount_max, currency, details, created_at, company_id, attachment_url, attachment_name, companies(name, description, industry, country)",
      )
      .eq("id", data.id)
      .eq("is_public", true)
      .eq("status", "active")
      .maybeSingle();
    if (error) throw error;
    if (!row) throw new Error("Not found");
    const { signAttachments } = await import("@/lib/listing-attachments.server");
    const [r] = (await signAttachments([row as any])) as any[];
    return {
      listing: {
        ...r,
        company_name: r.companies?.name ?? "Company",
        company_description: r.companies?.description ?? null,
        companies: undefined,
      },
    };
  });
